export type CaseDemo = {
  id: string;
  order: number;
  title: string;
  shortTitle: string;
  industry: string;
  promise: string;
  audience: string;
  realAi: string[];
  mock: string[];
  pages: string[];
  promptLabel: string;
  placeholder: string;
  sampleInput: string;
  primaryAction: string;
  metrics: { label: string; value: string }[];
  artifacts: { label: string; value: string; tone?: "green" | "blue" | "amber" }[];
  demoScript: string[];
};

export const cases: CaseDemo[] = [
  {
    id: "customer-service",
    order: 1,
    title: "AI 智能客服 / 对话机器人",
    shortTitle: "智能客服",
    industry: "B2B / SaaS / 电商",
    promise: "基于 FAQ 的多轮客服、流式回复和转人工留资。",
    audience: "适合需要降低一线客服压力、统一答复口径的企业。",
    realAi: ["多轮对话", "FAQ 受限回答", "无法回答时转人工"],
    mock: ["对话记录", "满意度评分", "热门问题统计"],
    pages: ["产品落地页", "后台会话管理", "数据看板"],
    promptLabel: "客户问题",
    placeholder: "例如：你们支持私有化部署吗？价格怎么计算？",
    sampleInput: "你们的智能客服可以接入企业微信和官网吗？如果不知道答案应该怎么办？",
    primaryAction: "咨询客服",
    metrics: [
      { label: "FAQ", value: "20 条" },
      { label: "平均响应", value: "1.8s" },
      { label: "转人工率", value: "12%" }
    ],
    artifacts: [
      { label: "客服策略", value: "只回答产品相关问题", tone: "green" },
      { label: "留资字段", value: "姓名 / 手机 / 需求", tone: "blue" }
    ],
    demoScript: ["连续问 3 轮产品问题", "问一个无关问题", "展示后台对话记录和满意度"]
  },
  {
    id: "ecommerce-recommendation",
    order: 2,
    title: "电商网站 + 智能推荐",
    shortTitle: "AI 导购",
    industry: "零售 / 品牌 / 电商",
    promise: "用自然语言描述需求，自动推荐商品和理由。",
    audience: "适合需要提升转化率和搜索体验的品牌商城。",
    realAi: ["AI 导购", "语义搜索", "推荐理由生成"],
    mock: ["商品列表", "购物车", "猜你喜欢"],
    pages: ["首页", "商品列表", "商品详情", "购物车", "AI 导购"],
    promptLabel: "购买需求",
    placeholder: "例如：送给爱运动的男朋友，预算 500 元",
    sampleInput: "送给经常跑步的男朋友，预算 500 元，希望实用、有质感。",
    primaryAction: "生成推荐",
    metrics: [
      { label: "商品库", value: "50 件" },
      { label: "推荐数量", value: "3-5 件" },
      { label: "转化提升", value: "+18%" }
    ],
    artifacts: [
      { label: "搜索方式", value: "语义 + 标签重排", tone: "green" },
      { label: "推荐解释", value: "预算 / 场景 / 偏好", tone: "blue" }
    ],
    demoScript: ["输入一句自然语言需求", "展示推荐商品和理由", "切到购物车和猜你喜欢"]
  },
  {
    id: "knowledge-rag",
    order: 3,
    title: "企业知识库问答平台",
    shortTitle: "知识库 RAG",
    industry: "企业 / 政府 / 律所 / 咨询",
    promise: "上传文档后基于内容问答，并标注引用来源。",
    audience: "适合想把内部制度、手册、合同知识变成内部 GPT 的组织。",
    realAi: ["文档切片", "向量检索", "带引用回答"],
    mock: ["知识库管理", "文档列表", "问答历史"],
    pages: ["上传管理", "问答对话", "知识库设置"],
    promptLabel: "知识库问题",
    placeholder: "例如：试用期请假流程是什么？引用哪份制度？",
    sampleInput: "根据员工手册，试用期员工如何申请请假？请列出引用来源。",
    primaryAction: "检索回答",
    metrics: [
      { label: "示例文档", value: "3 份" },
      { label: "引用片段", value: "Top 5" },
      { label: "拒答策略", value: "已启用" }
    ],
    artifacts: [
      { label: "引用格式", value: "文档名 + 片段", tone: "green" },
      { label: "库外问题", value: "不编造", tone: "amber" }
    ],
    demoScript: ["上传或选择示例文档", "提问制度类问题", "展示答案下方引用来源"]
  },
  {
    id: "content-generation",
    order: 4,
    title: "AI 内容生成平台",
    shortTitle: "内容生成",
    industry: "营销 / 电商运营 / 自媒体",
    promise: "多模板生成营销内容，并支持改写、扩写、缩写和换语气。",
    audience: "适合高频生产社媒、SEO 和商品文案的团队。",
    realAi: ["模板生成", "一键改写", "换语气"],
    mock: ["历史作品库", "收藏", "用量统计"],
    pages: ["模板选择", "生成工作台", "作品历史"],
    promptLabel: "内容需求",
    placeholder: "例如：为一款低糖气泡水生成小红书文案",
    sampleInput: "为一款低糖青柠气泡水生成小红书种草文案，语气轻松，150 字。",
    primaryAction: "生成文案",
    metrics: [
      { label: "模板", value: "6 类" },
      { label: "平均生成", value: "4.2s" },
      { label: "复用率", value: "64%" }
    ],
    artifacts: [
      { label: "模板", value: "小红书 / SEO / 邮件", tone: "green" },
      { label: "二次处理", value: "改写 / 扩写 / 缩写", tone: "blue" }
    ],
    demoScript: ["选择模板并输入产品卖点", "生成一版内容", "点击换语气生成第二版"]
  },
  {
    id: "data-text-sql",
    order: 5,
    title: "数据分析仪表盘 + 自然语言查询",
    shortTitle: "自然语言问数",
    industry: "管理层 / 运营 / 数据团队",
    promise: "用大白话生成只读 SQL、自动出图并给出结论。",
    audience: "适合传统企业和运营团队降低取数门槛。",
    realAi: ["Text-to-SQL", "图表选择", "结论生成"],
    mock: ["指标卡", "销售数据集", "趋势图"],
    pages: ["仪表盘首页", "自然语言问数"],
    promptLabel: "数据问题",
    placeholder: "例如：上个月哪个区域销量最高？",
    sampleInput: "上个月华东、华南、华北哪个区域销售额最高？请给出图表建议。",
    primaryAction: "生成分析",
    metrics: [
      { label: "示例数据", value: "1200 行" },
      { label: "SQL 安全", value: "只读" },
      { label: "图表类型", value: "自动" }
    ],
    artifacts: [
      { label: "SQL 白名单", value: "SELECT only", tone: "green" },
      { label: "恶意语句", value: "自动拦截", tone: "amber" }
    ],
    demoScript: ["输入销售问题", "展示 SQL 和图表", "测试写操作被拒绝"]
  },
  {
    id: "image-intelligence",
    order: 6,
    title: "多模态 / AI 图像应用",
    shortTitle: "图像理解",
    industry: "设计 / 电商 / 营销",
    promise: "上传商品图，自动描述、打标签并生成卖点文案。",
    audience: "适合需要从图片快速生成商品内容的电商和营销团队。",
    realAi: ["图像理解", "自动标签", "图生文案"],
    mock: ["作品画廊", "风格预设", "历史结果"],
    pages: ["功能入口", "分析工作台", "作品画廊"],
    promptLabel: "图片说明",
    placeholder: "例如：这是一张白底运动鞋商品图，请生成卖点",
    sampleInput: "这是一张白底运动鞋商品图，鞋面轻薄、适合夏季跑步，请生成标签和卖点文案。",
    primaryAction: "分析图片",
    metrics: [
      { label: "标签", value: "8 个" },
      { label: "文案版本", value: "3 版" },
      { label: "成本", value: "低" }
    ],
    artifacts: [
      { label: "图像模型", value: "多模态可替换", tone: "green" },
      { label: "联动", value: "内容生成平台", tone: "blue" }
    ],
    demoScript: ["上传商品图或使用示例图", "生成描述和标签", "输出商品卖点文案"]
  },
  {
    id: "education-tutor",
    order: 7,
    title: "在线教育 + AI 辅导",
    shortTitle: "AI 辅导",
    industry: "教育机构 / 学校 / 培训",
    promise: "AI 老师启发式答疑，并对作文/主观题结构化批改。",
    audience: "适合要提升答疑效率和个性化反馈的教育产品。",
    realAi: ["分步讲解", "启发式答疑", "作业批改"],
    mock: ["课程列表", "学习进度", "错题本"],
    pages: ["课程首页", "课程学习", "作业批改", "学习报告"],
    promptLabel: "学生问题或作文",
    placeholder: "例如：为什么二次函数有最值？请分步骤讲解",
    sampleInput: "请像初中老师一样解释为什么二次函数 y=x²-4x+3 有最小值。",
    primaryAction: "请求辅导",
    metrics: [
      { label: "课程", value: "5 门" },
      { label: "批改维度", value: "4 项" },
      { label: "答疑方式", value: "启发式" }
    ],
    artifacts: [
      { label: "评分", value: "内容 / 结构 / 语言", tone: "green" },
      { label: "错题", value: "自动归档", tone: "blue" }
    ],
    demoScript: ["输入一道题", "展示分步引导", "切到作文批改结果"]
  },
  {
    id: "hr-resume",
    order: 8,
    title: "招聘 / HR 平台 + 简历智能筛选",
    shortTitle: "简历筛选",
    industry: "HR / 招聘公司 / 企业人力",
    promise: "解析简历为结构化字段，并与岗位 JD 匹配评分。",
    audience: "适合需要批量初筛简历和解释匹配理由的招聘团队。",
    realAi: ["简历解析", "JD 匹配", "缺口分析"],
    mock: ["职位管理", "候选人看板", "面试安排"],
    pages: ["职位列表", "简历上传", "匹配看板"],
    promptLabel: "简历摘要或 JD",
    placeholder: "例如：3 年 React 经验，做过 B2B SaaS，希望匹配前端岗位",
    sampleInput: "候选人有 4 年 React/TypeScript 经验，做过数据看板和组件库，匹配高级前端工程师岗位。",
    primaryAction: "生成匹配分析",
    metrics: [
      { label: "结构字段", value: "12 项" },
      { label: "匹配分", value: "0-100" },
      { label: "缺口", value: "自动提取" }
    ],
    artifacts: [
      { label: "输出", value: "JSON schema", tone: "green" },
      { label: "看板", value: "候选人排序", tone: "blue" }
    ],
    demoScript: ["上传或粘贴简历", "选择岗位 JD", "展示匹配分和缺口分析"]
  },
  {
    id: "meeting-voice",
    order: 9,
    title: "语音应用：会议记录 / 智能转写",
    shortTitle: "会议转写",
    industry: "企业 / 律所 / 媒体 / 教育",
    promise: "音频转文字后生成会议纪要、待办和关键决策。",
    audience: "适合需要沉淀会议资产和快速追踪待办的团队。",
    realAi: ["ASR 转写", "纪要摘要", "会议问答"],
    mock: ["会议列表", "说话人分段", "示例音频"],
    pages: ["会议列表", "上传转写", "纪要详情"],
    promptLabel: "会议内容",
    placeholder: "例如：讨论了新官网上线、负责人和截止时间",
    sampleInput: "会议讨论了官网 6 月上线，设计由小王负责，后端接口由小李负责，下周三前联调。",
    primaryAction: "生成纪要",
    metrics: [
      { label: "示例音频", value: "2 段" },
      { label: "待办抽取", value: "自动" },
      { label: "问答", value: "支持" }
    ],
    artifacts: [
      { label: "纪要结构", value: "摘要 / 决策 / 待办", tone: "green" },
      { label: "说话人", value: "演示分段", tone: "amber" }
    ],
    demoScript: ["选择示例音频", "生成纪要和待办", "问谁负责某项任务"]
  },
  {
    id: "agent-workflow",
    order: 10,
    title: "AI Agent 工作流自动化",
    shortTitle: "Agent 自动化",
    industry: "企业流程 / 决策层",
    promise: "输入目标后，Agent 规划步骤、调用工具并透明展示执行过程。",
    audience: "适合想用 AI 替代重复流程、打通工具链的企业。",
    realAi: ["任务规划", "工具调用", "执行过程可视化"],
    mock: ["模板库", "工具结果", "中间状态"],
    pages: ["任务输入", "步骤流", "结果输出"],
    promptLabel: "目标任务",
    placeholder: "例如：调研一个行业并生成简报",
    sampleInput: "调研低空经济行业的三类典型客户，并生成一份销售跟进简报。",
    primaryAction: "启动 Agent",
    metrics: [
      { label: "内置工具", value: "3 个" },
      { label: "步骤", value: "可视化" },
      { label: "模板", value: "3 套" }
    ],
    artifacts: [
      { label: "工具", value: "搜索 / 计算 / 文档检索", tone: "green" },
      { label: "透明度", value: "思考 / 调用 / 结果", tone: "blue" }
    ],
    demoScript: ["选择一个任务模板", "展示规划和工具调用", "输出最终简报"]
  }
];

export function getCase(id: string) {
  return cases.find((item) => item.id === id) ?? cases[0];
}
